import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { Scissors, Wrench, TreeDeciduous, Construction, X, Sprout } from "lucide-react";

function getTopics(t) {
  return [
    {
      id: "potatura-verde",
      icon: Sprout,
      title: t("gestioneVite.topics.potaturaVerde.title"),
      short: t("gestioneVite.topics.potaturaVerde.short"),
      desc: t("gestioneVite.topics.potaturaVerde.desc"),
      bg: "#3A7D4A",
      when: t("gestioneVite.topics.potaturaVerde.when"),
    },
    {
      id: "cimatura",
      icon: Scissors,
      title: t("gestioneVite.topics.cimatura.title"),
      short: t("gestioneVite.topics.cimatura.short"),
      desc: t("gestioneVite.topics.cimatura.desc"),
      bg: "#D9822B",
      when: t("gestioneVite.topics.cimatura.when"),
    },
    {
      id: "potatura-invernale",
      icon: TreeDeciduous,
      title: t("gestioneVite.topics.potaturaInvernale.title"),
      short: t("gestioneVite.topics.potaturaInvernale.short"),
      desc: t("gestioneVite.topics.potaturaInvernale.desc"),
      bg: "#6B2F5E",
      when: t("gestioneVite.topics.potaturaInvernale.when"),
    },
  ];
}

export default function GestioneVite() {
  const { t } = useTranslation();
  const [openTopic, setOpenTopic] = useState(null);
  const TOPICS = getTopics(t);

  return (
    <div className="min-h-screen pb-24" data-testid="gestione-page">
      <Header title={t("gestioneVite.pageTitle")} subtitle={t("gestioneVite.pageSubtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Hero */}
        <section className="vs-card p-4 flex items-start gap-3"
          style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}>
            <Wrench size={22} color="white" />
          </div>
          <div>
            <h2 className="heading text-lg font-extrabold">{t("gestioneVite.heroTitle")}</h2>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
              {t("gestioneVite.heroDescPrefix")} <strong>{t("gestioneVite.heroDescBold")}</strong> {t("gestioneVite.heroDescSuffix")}
            </p>
          </div>
        </section>

        {/* Topic cards */}
        <div className="space-y-3">
          {TOPICS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setOpenTopic(t)}
                className="vs-card p-4 flex items-center gap-3 w-full text-left transition-transform hover:scale-[1.01]"
                style={{ borderLeft: `6px solid ${t.bg}` }}
                data-testid={`topic-${t.id}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: t.bg }}>
                  <Icon size={24} color="white" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="heading font-extrabold text-base" style={{ color: "var(--text-primary)" }}>
                    {t.title}
                  </div>
                  <div className="text-xs mb-0.5" style={{ color: "var(--text-muted)" }}>
                    {t.short}
                  </div>
                  <div className="text-xs font-semibold" style={{ color: t.bg }}>
                    📅 {t.when}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-center italic" style={{ color: "var(--text-muted)" }}>
          {t("gestioneVite.footerNote")}
        </p>
      </main>

      {/* Coming soon modal */}
      {openTopic && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: "rgba(17,20,17,0.55)" }}
          onClick={() => setOpenTopic(null)}>
          <div className="w-full sm:max-w-lg vs-card p-6"
            style={{ borderRadius: "16px 16px 0 0" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="topic-modal">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: openTopic.bg }}>
                  <openTopic.icon size={22} color="white" strokeWidth={2.2} />
                </div>
                <div>
                  <h3 className="heading text-xl font-extrabold">{openTopic.title}</h3>
                  <div className="text-xs font-semibold" style={{ color: openTopic.bg }}>
                    📅 {openTopic.when}
                  </div>
                </div>
              </div>
              <button onClick={() => setOpenTopic(null)} className="p-2"><X size={20} /></button>
            </div>

            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
              {openTopic.desc}
            </p>

            <div className="p-5 rounded-xl text-center"
              style={{ background: "var(--secondary)", border: "2px dashed var(--border)" }}>
              <Construction size={36} style={{ color: "var(--gold)" }} className="mx-auto mb-3" />
              <div className="heading font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>
                {t("gestioneVite.modalTitle")}
              </div>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {t("gestioneVite.modalDescPrefix")} <strong>{openTopic.title.toLowerCase()}</strong> {t("gestioneVite.modalDescSuffix")}
              </p>
              <p className="text-xs mt-3 italic" style={{ color: "var(--text-muted)" }}>
                {t("gestioneVite.modalAvailableSoon")}
              </p>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
